/**
 * ContentEngineApprovalCard
 *
 * Inline message-level approval surface for Content Engine workflow stages.
 *
 * Primary responsibilities:
 * - Render Content Engine stage previews inside Chat messages
 * - Continue the chat from approval and revision actions
 *
 * Dependencies:
 * - AI Elements Confirmation
 * - Shared dialog, card, badge, and button primitives
 */

'use client'

import { useMemo, useState } from 'react'
import { IconCpu } from '@tabler/icons-react'
import { AlertCircle, CheckCircle2, FileText, ImageIcon, Maximize2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep
} from '@/components/ai-elements/chain-of-thought'
import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRejected,
  ConfirmationRequest,
  ConfirmationTitle,
  type ConfirmationState
} from '@/components/ai-elements/confirmation'
import { cn } from '@/lib/utils'

type ContentEngineToolKey =
  | 'ContentEngine__createCampaignDraft'
  | 'ContentEngine__approveCampaignAndCreateAssetPack'
  | 'ContentEngine__approveAssetPackAndGenerateVisuals'
  | 'ContentEngine__updateCampaign'

type ContentEngineStage =
  | 'campaign_draft_review'
  | 'asset_pack_review'
  | 'visuals_generated'
  | 'linked_to_space'
  | 'campaign_update_review'
  | 'unknown'

type ContentEnginePreview = {
  actionLabel: string | null
  approvalContinuationPayload: string | null
  campaignId: string | null
  campaignTitle: string
  description: string
  error: string | null
  generatedAssetCount: number
  key: ContentEngineToolKey
  outputRecordId: string | null
  outputTitle: string
  previewRows: Array<{ label: string; value: string }>
  requestChangesPrompt: string
  requiresApproval: boolean
  stage: ContentEngineStage
  state: ConfirmationState
  title: string
}

export interface ContentEngineApprovalCardProps {
  annotation: unknown
  className?: string
  disabled?: boolean
  onConfirmInteractive?: (payload: string) => void
}

const CONTENT_ENGINE_KEYS: ContentEngineToolKey[] = [
  'ContentEngine__createCampaignDraft',
  'ContentEngine__approveCampaignAndCreateAssetPack',
  'ContentEngine__approveAssetPackAndGenerateVisuals',
  'ContentEngine__updateCampaign'
]

function parseRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }

  if (typeof value !== 'string') {
    return null
  }

  try {
    const parsed = JSON.parse(value) as unknown
    return parseRecord(parsed)
  } catch {
    return null
  }
}

function normalizeAnnotation(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    return value.reduce<Record<string, unknown>>((acc, item) => {
      const record = parseRecord(item)
      if (!record) return acc

      return {
        ...acc,
        ...record
      }
    }, {})
  }

  return parseRecord(value) || {}
}

function getToolResultRecord(annotation: Record<string, unknown>, key: ContentEngineToolKey) {
  const entry = parseRecord(annotation[key])
  if (!entry) return null

  return parseRecord(entry.result) || entry
}

function getString(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return typeof value === 'string' ? value.trim() : ''
}

function getNestedRecord(record: Record<string, unknown>, key: string) {
  return parseRecord(record[key]) || {}
}

function getArrayLength(value: unknown) {
  return Array.isArray(value) ? value.length : 0
}

function getGeneratedAssetCount(record: Record<string, unknown>, output: Record<string, unknown>) {
  const directCount = getArrayLength(record.generatedAssets)
  if (directCount > 0) return directCount

  const assetPack = getNestedRecord(output, 'assetPack')
  const visuals = getNestedRecord(assetPack, 'visuals')
  return getArrayLength(visuals.generatedAssets)
}

function getStage(record: Record<string, unknown>, key: ContentEngineToolKey): ContentEngineStage {
  const stage = getString(record, 'stage')
  if (
    stage === 'campaign_draft_review' ||
    stage === 'asset_pack_review' ||
    stage === 'visuals_generated' ||
    stage === 'linked_to_space'
  ) {
    return stage
  }

  if (key === 'ContentEngine__createCampaignDraft') return 'campaign_draft_review'
  if (key === 'ContentEngine__approveCampaignAndCreateAssetPack') return 'asset_pack_review'
  if (key === 'ContentEngine__approveAssetPackAndGenerateVisuals') return 'visuals_generated'
  if (key === 'ContentEngine__updateCampaign') return 'campaign_update_review'

  return 'unknown'
}

function buildPreviewRows(params: {
  campaign: Record<string, unknown>
  output: Record<string, unknown>
  generatedAssetCount: number
}) {
  const assetPack = getNestedRecord(params.output, 'assetPack')
  const strategy = getNestedRecord(assetPack, 'strategy')
  const blog = getNestedRecord(assetPack, 'blog')
  const social = getNestedRecord(assetPack, 'social')
  const visuals = getNestedRecord(assetPack, 'visuals')

  return [
    { label: 'Campaign', value: getString(params.campaign, 'name') },
    { label: 'Topic', value: getString(params.campaign, 'topic') },
    { label: 'Objective', value: getString(params.campaign, 'primaryObjective') },
    { label: 'Audience', value: getString(params.campaign, 'audience') },
    { label: 'Message', value: getString(params.campaign, 'keyMessage') },
    { label: 'Asset pack', value: getString(params.output, 'name') },
    {
      label: 'Strategy',
      value: getString(strategy, 'bigIdea') || getString(strategy, 'coreNarrative')
    },
    { label: 'Blog', value: getString(blog, 'title') },
    {
      label: 'Social drafts',
      value: String(
        getArrayLength(social.linkedin) +
          getArrayLength(social.x) +
          getArrayLength(social.instagramCaptions)
      )
    },
    { label: 'Visual prompts', value: String(getArrayLength(visuals.imagePrompts)) },
    { label: 'Generated visuals', value: String(params.generatedAssetCount) }
  ].filter((row) => row.value && row.value !== '0')
}

function buildApprovalContinuationPayload(params: {
  campaignId: string | null
  outputRecordId: string | null
  stage: ContentEngineStage
}) {
  if (params.stage === 'asset_pack_review' && params.outputRecordId) {
    return `I approve Content Engine asset pack ${params.outputRecordId}. Generate and save the campaign visuals now.`
  }

  if (params.stage === 'campaign_draft_review' && params.campaignId) {
    return `I approve Content Engine campaign ${params.campaignId}. Create and save the campaign asset pack for review now.`
  }

  if (params.stage === 'campaign_update_review' && params.campaignId) {
    return `I approve the Content Engine campaign update for ${params.campaignId}. Save it now.`
  }

  return null
}

function buildPreview(
  key: ContentEngineToolKey,
  record: Record<string, unknown>
): ContentEnginePreview | null {
  const campaign = getNestedRecord(record, 'campaign')
  const output = getNestedRecord(record, 'outputRecord')
  const campaignId =
    getString(record, 'campaignId') ||
    getString(record, 'campaign_id') ||
    getString(campaign, 'id') ||
    null
  const outputRecordId =
    getString(record, 'outputRecordId') ||
    getString(record, 'output_record_id') ||
    getString(output, 'id') ||
    null
  let stage = getStage(record, key)
  if (
    key === 'ContentEngine__approveCampaignAndCreateAssetPack' &&
    (record.requiresConfirmation === true || record.requiresContent === true)
  ) {
    stage = 'campaign_draft_review'
  }
  if (
    key === 'ContentEngine__approveAssetPackAndGenerateVisuals' &&
    record.requiresConfirmation === true
  ) {
    stage = 'asset_pack_review'
  }
  const error = getString(record, 'error') || null
  const generatedAssetCount = getGeneratedAssetCount(record, output)
  const requiresApproval =
    record.requiresConfirmation === true ||
    record.requiresContent === true ||
    stage === 'campaign_draft_review' ||
    stage === 'asset_pack_review' ||
    stage === 'campaign_update_review'
  const failed = Boolean(error) || (record.success === false && !requiresApproval)
  const state: ConfirmationState = failed
    ? 'output-denied'
    : requiresApproval
      ? 'approval-requested'
      : 'output-available'
  const campaignTitle =
    getString(campaign, 'name') ||
    getString(campaign, 'topic') ||
    (campaignId ? `Campaign ${campaignId.slice(0, 8)}` : 'Campaign')
  const outputTitle =
    getString(output, 'name') ||
    getString(output, 'sourceCampaignName') ||
    (outputRecordId ? `Asset pack ${outputRecordId.slice(0, 8)}` : 'Asset pack')
  const approvalContinuationPayload = buildApprovalContinuationPayload({
    campaignId,
    outputRecordId,
    stage
  })

  if (!campaignId && !outputRecordId && !error) {
    return null
  }

  return {
    actionLabel:
      stage === 'asset_pack_review'
        ? 'Approve asset pack'
        : stage === 'campaign_draft_review'
          ? 'Approve campaign'
          : stage === 'campaign_update_review'
            ? 'Approve update'
            : null,
    approvalContinuationPayload,
    campaignId,
    campaignTitle,
    description:
      stage === 'asset_pack_review'
        ? 'Review the asset pack before Losi generates campaign visuals.'
        : stage === 'campaign_draft_review'
          ? 'Review the campaign draft before Losi creates the asset pack.'
          : stage === 'visuals_generated'
            ? 'Campaign visuals were generated and saved to Content Engine.'
            : 'Review the Content Engine stage before continuing.',
    error,
    generatedAssetCount,
    key,
    outputRecordId,
    outputTitle,
    previewRows: buildPreviewRows({ campaign, output, generatedAssetCount }),
    requestChangesPrompt:
      stage === 'asset_pack_review' && outputRecordId
        ? `Do not approve Content Engine asset pack ${outputRecordId} yet. Help me revise it in chat first.`
        : campaignId
          ? `Do not approve Content Engine campaign ${campaignId} yet. Help me revise it in chat first.`
          : 'Do not approve this Content Engine stage yet. Help me revise it in chat first.',
    requiresApproval,
    stage,
    state,
    title:
      stage === 'asset_pack_review'
        ? 'Asset pack ready for approval'
        : stage === 'campaign_draft_review'
          ? 'Campaign draft ready for approval'
          : stage === 'visuals_generated'
            ? 'Campaign visuals generated'
            : failed
              ? 'Content Engine needs attention'
              : 'Content Engine stage ready'
  }
}

function getPreviewFromAnnotation(annotation: unknown) {
  const normalized = normalizeAnnotation(annotation)

  return CONTENT_ENGINE_KEYS.map((key) => {
    const result = getToolResultRecord(normalized, key)
    return result ? buildPreview(key, result) : null
  }).filter((preview): preview is ContentEnginePreview => preview !== null)
}

function PreviewRows({
  limit,
  rows
}: {
  limit?: number
  rows: ContentEnginePreview['previewRows']
}) {
  if (rows.length === 0) {
    return null
  }

  const visibleRows = typeof limit === 'number' ? rows.slice(0, limit) : rows

  return (
    <div className="grid gap-1.5 text-xs sm:grid-cols-2">
      {visibleRows.map((row) => (
        <div key={row.label} className="border-border/60 bg-muted/25 rounded-md border p-2">
          <div className="text-muted-foreground">{row.label}</div>
          <div className="line-clamp-2 font-medium">{row.value}</div>
        </div>
      ))}
    </div>
  )
}

function ContentEnginePreviewDialog({
  preview,
  open,
  onOpenChange
}: {
  preview: ContentEnginePreview
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{preview.title}</DialogTitle>
          <DialogDescription>{preview.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <PreviewRows rows={preview.previewRows} />
          <div className="border-border/60 bg-muted/25 rounded-md border p-3 text-xs">
            <div className="font-medium">Workflow position</div>
            <div className="text-muted-foreground mt-1">{preview.stage.replace(/_/g, ' ')}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ContentEngineApprovalItem({
  preview,
  disabled,
  onConfirmInteractive
}: {
  disabled?: boolean
  onConfirmInteractive?: (payload: string) => void
  preview: ContentEnginePreview
}) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const approval = {
    id: `${preview.key}:${preview.campaignId || preview.outputRecordId || preview.stage}`,
    approved:
      preview.state === 'output-available' ? true : preview.state === 'output-denied' ? false : null
  }
  const canApprove = Boolean(
    preview.approvalContinuationPayload &&
      onConfirmInteractive &&
      !disabled
  )

  return (
    <Card className="not-prose border-border/70 bg-background w-full max-w-2xl p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-md">
          <IconCpu className="size-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold">Content Engine</div>
            <Badge variant={preview.requiresApproval ? 'outline' : 'secondary'}>
              {preview.requiresApproval ? 'Needs approval' : 'Approved'}
            </Badge>
            <Badge variant="outline">{preview.stage.replace(/_/g, ' ')}</Badge>
          </div>

          <Confirmation
            approval={approval}
            className="border-border/60 bg-muted/25 [&>svg~*]:pl-0"
            state={preview.state}
          >
            <ConfirmationTitle>{preview.title}</ConfirmationTitle>
            <ConfirmationRequest>{preview.description}</ConfirmationRequest>
            <ConfirmationAccepted className="flex items-center gap-2">
              <CheckCircle2 className="size-3.5" />
              <span>{preview.description}</span>
            </ConfirmationAccepted>
            <ConfirmationRejected className="text-destructive flex items-center gap-2">
              <AlertCircle className="size-3.5" />
              <span>{preview.error || 'This Content Engine stage did not complete.'}</span>
            </ConfirmationRejected>
            <ConfirmationActions>
              <ConfirmationAction
                disabled={disabled || !onConfirmInteractive}
                variant="outline"
                onClick={() => onConfirmInteractive?.(preview.requestChangesPrompt)}
              >
                Request changes
              </ConfirmationAction>
              {preview.actionLabel && (
                <ConfirmationAction
                  disabled={!canApprove}
                  onClick={() => {
                    if (preview.approvalContinuationPayload) {
                      onConfirmInteractive?.(preview.approvalContinuationPayload)
                    }
                  }}
                >
                  {preview.actionLabel}
                </ConfirmationAction>
              )}
            </ConfirmationActions>
          </Confirmation>

          <ChainOfThought defaultOpen>
            <ChainOfThoughtHeader>Engine stages</ChainOfThoughtHeader>
            <ChainOfThoughtContent>
              <ChainOfThoughtStep
                icon={CheckCircle2}
                label="Campaign draft"
                status={preview.stage === 'campaign_draft_review' ? 'active' : 'complete'}
              />
              <ChainOfThoughtStep
                icon={FileText}
                label="Asset pack"
                status={
                  preview.stage === 'asset_pack_review'
                    ? 'active'
                    : preview.stage === 'visuals_generated' || preview.stage === 'linked_to_space'
                      ? 'complete'
                      : 'pending'
                }
              />
              <ChainOfThoughtStep
                icon={ImageIcon}
                label="Visuals"
                status={
                  preview.stage === 'visuals_generated' || preview.stage === 'linked_to_space'
                    ? 'complete'
                    : 'pending'
                }
              />
            </ChainOfThoughtContent>
          </ChainOfThought>

          <PreviewRows rows={preview.previewRows} limit={6} />

          <div className="flex flex-wrap gap-2">
            <Button
              className="h-8 text-xs"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setPreviewOpen(true)}
            >
              <Maximize2 className="size-3.5" />
              Open preview
            </Button>
          </div>
        </div>
      </div>
      <ContentEnginePreviewDialog
        open={previewOpen}
        preview={preview}
        onOpenChange={setPreviewOpen}
      />
    </Card>
  )
}

export function ContentEngineApprovalCard({
  annotation,
  className,
  disabled,
  onConfirmInteractive
}: ContentEngineApprovalCardProps) {
  const previews = useMemo(() => getPreviewFromAnnotation(annotation), [annotation])

  if (previews.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {previews.map((preview) => (
        <ContentEngineApprovalItem
          key={`${preview.key}-${preview.campaignId || preview.outputRecordId || preview.stage}`}
          disabled={disabled}
          preview={preview}
          onConfirmInteractive={onConfirmInteractive}
        />
      ))}
    </div>
  )
}
